import { CommonModule, TitleCasePipe } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import saveAs from 'file-saver';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { DialogComponent } from "../../../../../@core/components/ui/dialog/dialog.component";
import { PopoverComponent } from "../../../../../@core/components/ui/popover/popover.component";
import { ProgressBarModule } from 'primeng/progressbar';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { CheckboxComponent } from "../../../../../@core/components/ui/checkbox/checkbox.component";
import { DeleteConfirmationComponent } from '../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { IAttachment, IFile } from '../../../../../@core/models/model';
import { SOCKET_EVENTS, SocketService } from "../../../../../@core/services/socket.service";
import { ToastService } from "../../../../../@core/services/toast.service";
import { DataTableComponent } from "../../../../../@shared/components/data-table/data-table.component";
import { DocumentPreviewComponent } from "../../../../../@shared/components/document-preview/document-preview.component";
import { SearchBarComponent } from "../../../../../@shared/components/search-bar/search-bar.component";
import { UploadDocsComponent } from '../../../../../@shared/components/upload-docs/upload-docs.component';
import { TableColumn, TableConfig } from "../../../../../@shared/models/table.models";
import { SanitizeHtmlPipe } from "../../../../../@shared/pipes/sanitizeHtml.pipe";
import { UserAvatarPipe } from "../../../../../@shared/pipes/user-avatar-pipe";
import { EMBEDDINGS_GENERATION_STATUS, KNOWLEDGE_SCOPE } from "../../../../models/contract-management.model";
import { IKnowledgeBaseStore, KnowledgeBaseService } from '../../../../services/knowledgebase.service';
import { RepositoryService } from '../../../../services/repository.service';
import { StatusBadgeComponent } from "../components/status-badge/status-badge.component";
import { KNOWLEDGE_BASE_SVG } from '../knowledge-base-svg';
import { FILES_TABLE_COLUMNS, FILES_TABLE_CONFIG } from "./files-table.config";
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';

@Component({
  selector: 'app-files',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    SkeletonModule,
    TooltipModule,
    ProgressBarModule,
    AvatarModule,
    ButtonModule,
    InputGroupModule,
    InputTextModule,
    InputGroupAddonModule,
    TableModule,
    SanitizeHtmlPipe,
    CheckboxModule,
    StatusBadgeComponent,
    DocumentPreviewComponent,
    DialogComponent,
    PopoverComponent,
    DataTableComponent,
    SearchBarComponent,
    TitleCasePipe,
    ButtonComponent,
    CheckboxComponent,
    DeleteConfirmationComponent,
    AssetUrlPipe,
    UserNameDisplayComponent
  ],
  standalone: true,
  templateUrl: './files.component.html',
  styleUrl: './files.component.scss'
})
export class FilesComponent implements OnDestroy, AfterViewInit, OnChanges {

  @Input() projectId: string | null = null;
  @Input() scope: KNOWLEDGE_SCOPE = KNOWLEDGE_SCOPE.WORKSPACE;

  @ViewChild(DataTableComponent) dataTable!: DataTableComponent;

  // Template References
  @ViewChild('fileTemplateRef') fileTemplateRef!: TemplateRef<any>;
  @ViewChild('userTemplateRef') userTemplateRef!: TemplateRef<any>;
  @ViewChild('knowledgeBaseTemplateRef') knowledgeBaseTemplateRef!: TemplateRef<any>;
  @ViewChild('numberOfTimesUsedTemplateRef') numberOfTimesUsedTemplateRef!: TemplateRef<any>;
  @ViewChild('dateTemplateRef') dateTemplateRef!: TemplateRef<any>;
  @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;

  files: IFile[] = []
  filteredFiles: IFile[] = []
  selectedFiles: IFile[] = []
  selectedFilesIds: string[] = []
  showUploadDialog = false;
  searchValue: string = '';
  linkToKnowledgeBaseDialog: boolean = false;

  // Table Configuration
  tableColumns: TableColumn[] = [...FILES_TABLE_COLUMNS];
  tableConfig: TableConfig = { ...FILES_TABLE_CONFIG };

  knowledgeBases: any[] = []
  filteredKnowledgeBases: any[] = []
  knowledgeBaseSearchValue: string = '';
  deleteFileFromKnowledgeBase: boolean = false

  SVGS = KNOWLEDGE_BASE_SVG;

  createNewKnowledgeBaseDialog: boolean = false;
  knowledgeBaseForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
  });

  uploadedFiles: File[] = [];
  showDocumentPreview: boolean = false;
  selectedDocumentFile: IAttachment | null = null;
  selectedDocumentId: string | null = null;

  private embeddingsStatusSubscription!: Subscription;
  EMBEDDINGS_GENERATION_STATUS = EMBEDDINGS_GENERATION_STATUS;

  constructor(
    private dialog: MatDialog,
    private repositoryService: RepositoryService,
    private knowledgeBaseService: KnowledgeBaseService,
    private toast: ToastService,
    private socketService: SocketService
  ) { }

  ngOnInit() {
    this.getFiles();
    this.setupSocketListeners();
  }

  ngOnChanges(changes: SimpleChanges) {
    const projectIdChanged = changes['projectId'] && !changes['projectId'].firstChange;
    const filesTypeChanged = changes['filesType'] && !changes['filesType'].firstChange;
    if (projectIdChanged || filesTypeChanged) {
      this.getFiles();
    }
  }

  ngAfterViewInit() {
    // Build templates object for data table
    this.buildTemplatesMap();
  }

  get templatesMap(): { [key: string]: TemplateRef<any> } {
    return {
      'fileTemplate': this.fileTemplateRef,
      'userTemplate': this.userTemplateRef,
      'knowledgeBaseTemplate': this.knowledgeBaseTemplateRef,
      'numberOfTimesUsedTemplate': this.numberOfTimesUsedTemplateRef,
      'dateTemplate': this.dateTemplateRef,
      'actionsTemplate': this.actionsTemplateRef
    };
  }

  private buildTemplatesMap() {
    // This method can be used for any additional template setup if needed
  }

  setupSocketListeners() {
    this.embeddingsStatusSubscription = this.socketService
      .on<{ documentKnowledge: string, status: EMBEDDINGS_GENERATION_STATUS, file?: IFile }>(SOCKET_EVENTS.EMBEDDINGS_GENERATION_STATUS_CHANGED)
      .subscribe(res => {
        if (res && res[0]) {
          const { documentKnowledge, status, file } = res[0];
          if (file) {
            this.updateFileStatus(file._id, status);
          } else if (documentKnowledge) {
            this.updateFileStatusByDocumentKnowledge(documentKnowledge, status);
          }
          if (status === EMBEDDINGS_GENERATION_STATUS.FAILED) {
            this.toast.error('One or more files appear to be corrupted. Please re-upload valid files');
          }
        }
      });
  }

  updateFileStatus(fileId: string, status: EMBEDDINGS_GENERATION_STATUS) {
    this.files = this.files.map(file => {
      if (file._id === fileId) {
        return { ...file, documentKnowledge: { ...file.documentKnowledge, embeddingsStatus: status } } as IFile
      }
      return file
    })
    this.filteredFiles = this.files
  }

  updateFileStatusByDocumentKnowledge(documentKnowledgeId: string, status: EMBEDDINGS_GENERATION_STATUS) {
    const fileIndex = this.files.findIndex(file => file.documentKnowledge?._id === documentKnowledgeId);
    if (fileIndex !== -1) {
      this.files[fileIndex].documentKnowledge = {
        ...this.files[fileIndex].documentKnowledge,
        embeddingsStatus: status
      } as any;

      const filteredFileIndex = this.filteredFiles.findIndex(file => file.documentKnowledge?._id === documentKnowledgeId);
      if (filteredFileIndex !== -1) {
        this.filteredFiles[filteredFileIndex].documentKnowledge = {
          ...this.filteredFiles[filteredFileIndex].documentKnowledge,
          embeddingsStatus: status
        } as any;
      }
    }
  }

  getSkeletonCount() {
    return [];
  }

  getFiles() {
    this.tableConfig = { ...this.tableConfig, loading: true };
    const params = {
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {}),
      scope: this.scope
    }
    console.log(params);
    this.repositoryService.getFiles(params).subscribe((res) => {
      this.files = res.data;
      this.files = this.files.filter(file => file.type == 'FILE');
      this.filteredFiles = [...this.files];
      this.tableConfig = { ...this.tableConfig, loading: false };
    });
  }

  uploadFile() {
    this.dialog.open(UploadDocsComponent, {
      data: {
        origin: 'forFiles',
        type: 'file',
        customTitle: 'Upload Files',
        project: this.projectId,
        scope: this.scope
      },
      width: '36rem',
      disableClose: true,
    }).afterClosed().subscribe((res) => {
      if (res) {
        this.getFiles();
      }
    });
  }

  onSearch() {
    if (!this.searchValue.trim()) {
      this.filteredFiles = [...this.files]
      this.selectedFiles = []
      return
    }

    const searchTerm = this.searchValue.toLowerCase().trim()

    this.filteredFiles = this.files.filter(file => {
      return (
        file.name?.toLowerCase().includes(searchTerm) ||
        file.mimeType?.toLowerCase().includes(searchTerm) ||
        file.createdBy?.fullName?.toLowerCase().includes(searchTerm)
      )
    })

    this.selectedFiles = []
  }

  onKnowledgeBaseSearch() {
    if (!this.knowledgeBaseSearchValue.trim()) {
      this.filteredKnowledgeBases = [...this.knowledgeBases]
      return
    }

    const searchTerm = this.knowledgeBaseSearchValue.toLowerCase().trim()

    this.filteredKnowledgeBases = this.knowledgeBases.filter(kb => {
      return (
        kb.name?.toLowerCase().includes(searchTerm) ||
        kb.description?.toLowerCase().includes(searchTerm)
      )
    })
  }

  downloadFile(file: IFile) {
    this.repositoryService.downloadRepoFiles([file._id]).subscribe(res => {
      if (res.success && res.data) {
        const blob = new Blob([new Uint8Array(res.data.data)], { type: 'application/x-zip-compressed' });
        saveAs(blob, `${file.name}.zip`);
      }
    })
  }

  deleteFile(file: IFile) {
    this.repositoryService.deleteFile(file._id).subscribe((res) => {
      if (res.success) {
        this.toast.success("File Deleted Succesfully");
        this.getFiles();
        this.deleteFileFromKnowledgeBase = false;
        this.fileToBeDeleted = null;
      }
    })
  }

  viewFile(file: IFile) {
  }

  deLinkFile(file: IFile) {
  }

  downloadFiles() {
    const selectedFileIds = this.selectedFiles.map(file => file._id);
    this.repositoryService.downloadRepoFiles(selectedFileIds).subscribe(res => {
      if (res.success && res.data) {
        const blob = new Blob([new Uint8Array(res.data.data)], { type: 'application/x-zip-compressed' });
        saveAs(blob, 'InnDoc AI Files Download.zip');
        this.selectedFiles = [];
        this.dataTable.clearSelection();
      }
    })
  }

  deleteSelectedFiles() {
    const selectedFileIds = this.selectedFiles.map(files => files._id);
    this.repositoryService.deleteFile(selectedFileIds).subscribe((res) => {
      if (res.success) {
        this.toast.success("File(s) Deleted Succesfully");
        this.getFiles();
        this.deleteFileFromKnowledgeBase = false;
        this.selectedFiles = [];
        this.dataTable.clearSelection();
      }
    })

  }

  linkToKnowledgeBase() {
    const selectedKnowledgeBases = this.filteredKnowledgeBases
      .filter(kb => kb.isSelected)
      .map(kb => kb._id);

    const selectedFileIds = this.selectedFiles.map(file => file._id);

    if (selectedKnowledgeBases.length === 0) {
      this.toast.error('Please select at least one knowledge base');
      return;
    }

    if (selectedFileIds.length === 0) {
      this.toast.error('Please select at least one file');
      return;
    }

    this.repositoryService.linkToKnowledgeBase(selectedKnowledgeBases, selectedFileIds).subscribe({
      next: (res) => {
        if (res.success) {
          const allAlreadyLinked = res.data?.results?.every((result: any) =>
            !result.success && result.message?.includes('were already linked')
          );

          if (allAlreadyLinked) {
            this.toast.info('Selected files were already linked to the knowledge bases');
          } else {
            this.toast.success('Files linked to knowledge base successfully');
          }

          this.linkToKnowledgeBaseDialog = false;
          this.selectedFiles = [];
          this.dataTable.clearSelection();
          this.getFiles();
        }
      },
      error: (err) => {
        this.toast.error('Failed to link files to knowledge base');
      }
    });
  }

  openLinkToKnowlegeBaseDialog(file?: IFile) {
    // If file is provided from menu, select it
    if (file) {
      this.selectedFiles = [file];
      this.selectedFilesIds = [file._id];
      // Update data table selection if available
      if (this.dataTable) {
        // Find the file in the data table and select it
        const fileInTable = this.filteredFiles.find(f => f._id === file._id);
        if (fileInTable) {
          // Check if already selected using dataKey comparison (same logic as data table)
          const isAlreadySelected = this.dataTable.selectedRows.some(selected =>
            selected._id === fileInTable._id
          );
          if (!isAlreadySelected) {
            this.dataTable.selectedRows = [fileInTable];
            this.dataTable.selectionChange.emit([fileInTable]);
          }
        }
      }
    }
    this.linkToKnowledgeBaseDialog = true;
    this.getKnowledgeBaseStore();
  }

  getKnowledgeBaseStore() {
    const filters = {
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {})
    }
    this.knowledgeBaseService.getKnowledgeBaseStore(filters).subscribe((res) => {
      this.knowledgeBases = res.data.stores.map(kb => ({ ...kb, isSelected: false }));
      this.filteredKnowledgeBases = [...this.knowledgeBases];
    });
  }

  createNewKnowledgeBase() {
    this.createNewKnowledgeBaseDialog = true;
  }

  onFileUploaded(event: File | File[]) {
    event = event as File[]
    if (event.length) {
      this.uploadedFiles = event;
    }
    else {
      this.uploadedFiles = [];
    }
  }

  createKnowledgeBaseStore() {
    const formData = new FormData();
    formData.append('name', this.knowledgeBaseForm.value.name);
    formData.append('description', this.knowledgeBaseForm.value.description);
    formData.append('scope', this.scope);
    if (this.scope === KNOWLEDGE_SCOPE.PROJECT && this.projectId) {
      formData.append('project', this.projectId);
    }
    if (this.uploadedFiles.length) {
      this.uploadedFiles.forEach(file => {
        formData.append('files', file);
      })
    }

    this.knowledgeBaseService.createKnowledgeBaseStore(formData).subscribe((res) => {
      if (res.data) {
        this.createNewKnowledgeBaseDialog = false;
        this.getKnowledgeBaseStore();
        this.toast.success('Knowledge Base Store created successfully', 'success');
        this.knowledgeBaseForm.reset();
        this.uploadedFiles = [];
      }
    });
  }

  toggleKnowledgeBaseSelection(knowledgeBase: any) {
    knowledgeBase.isSelected = !knowledgeBase.isSelected;
  }

  fileToBeDeleted: any

  openDeleteDialogBox(file?: IFile) {
    this.fileToBeDeleted = file

    this.deleteFileFromKnowledgeBase = true
  }
  ngOnDestroy() {
    this.embeddingsStatusSubscription.unsubscribe();
  }

  public selectedFile: IFile | null = null;

  openDocumentPreview(file: IFile) {
    this.selectedFile = file;
    this.selectedDocumentFile = file.attachment as IAttachment
    this.selectedDocumentId = file._id
    this.showDocumentPreview = true;
  }

  closeDocumentPreview() {
    this.selectedFile = null;
    this.selectedDocumentFile = null;
    this.selectedDocumentId = null;
    this.showDocumentPreview = false;
  }


  getKnowledgeBasesForFile(file: IFile): Array<{ _id: string, name: string }> {
    return file.knowledgeBases && file.knowledgeBases.length > 0
      ? file.knowledgeBases.map((kb: IKnowledgeBaseStore) => ({ _id: kb._id, name: kb.name }))
      : [];
  }

  // Table-specific methods
  getRowClass = (rowData: any, index: number): string => {
    return this.selectedFiles.includes(rowData) ? 'bg-orange-100' : '';
  }

  onSelectionChange(selection: any[]) {
    this.selectedFiles = selection;
    this.selectedFilesIds = selection.map(file => file._id);
  }
}
